import { useCallback, useState } from "react";
import type { components } from "@connect/data/public/schema";
import { hasCollectionScreen, shouldShowScreen } from "./filter-screens";
import { COLLECTION_SCREEN_KEYS } from "./kyc-screen-keys";

export type OnboardingPhase =
  | "auth"
  | "kyc"
  | "processing"
  | "finalizing"
  | "terminal"
  | "unavailable";

export type CustomerKycRoute = "collect" | "challenge" | "wait" | "finalize" | "unavailable";

type CustomerKyc = components["schemas"]["CustomerKyc"];
type Requirements = components["schemas"]["Requirements"];

export function hasOutstandingKycRequirements(requirements: Requirements): boolean {
  return COLLECTION_SCREEN_KEYS.some((key) => shouldShowScreen(key, requirements));
}

export function hostedKycRequirementKeys(requirements: Requirements): (keyof Requirements)[] {
  return (Object.keys(requirements) as (keyof Requirements)[])
    .filter(
      (key) =>
        requirements[key]?.status === "incomplete" && !hasCollectionScreen(key, requirements),
    )
    .sort();
}

export function hostedChallengeKey(
  status: CustomerKyc["status"] | undefined,
  requirements: Requirements | undefined,
): string {
  return `${status}:${hostedKycRequirementKeys(requirements ?? {}).join()}`;
}

export function hasHostedKycRequirements(requirements: Requirements): boolean {
  return hostedKycRequirementKeys(requirements).length > 0;
}

const IDENTITY_SDK_REQUIREMENTS = new Set<keyof Requirements>([
  "identityDocuments",
  "selfie",
  "proofOfAddress",
]);

export function hasOnlyIdentitySdkRequirements(requirements: Requirements): boolean {
  const keys = hostedKycRequirementKeys(requirements);

  return keys.length > 0 && keys.every((key) => IDENTITY_SDK_REQUIREMENTS.has(key));
}

function routeOutstandingRequirements(requirements: Requirements): CustomerKycRoute {
  if (hasOutstandingKycRequirements(requirements)) {
    return "collect";
  }

  if (hasHostedKycRequirements(requirements)) {
    return "challenge";
  }

  return "wait";
}

export function routeCustomerKyc(
  kyc: Pick<CustomerKyc, "status" | "requirements" | "challenge">,
): CustomerKycRoute {
  if (kyc.challenge) {
    return "wait";
  }
  switch (kyc.status) {
    case "active":
      return "finalize";
    case "unavailable":
      return "unavailable";
    case "not_created":
      return "collect";
    case "collecting":
      return routeOutstandingRequirements(kyc.requirements);
    case "verifying":
      return hasOutstandingKycRequirements(kyc.requirements) ? "collect" : "wait";
  }
}

export function isInFlight(status: CustomerKyc["status"] | undefined): boolean {
  return status === "verifying" || status === "collecting";
}

export interface UseOnboardingOrchestratorParams {
  phase?: OnboardingPhase;
  kycIndex?: number;
}

interface OrchestratorState {
  phase: OnboardingPhase;
  kycIndex: number;
}

export function useOnboardingOrchestrator(initial: UseOnboardingOrchestratorParams = {}) {
  const [state, setState] = useState<OrchestratorState>({
    phase: initial.phase ?? "auth",
    kycIndex: initial.kycIndex ?? 0,
  });

  const enterKyc = useCallback((index = 0) => {
    setState({ phase: "kyc", kycIndex: index });
  }, []);
  const enterProcessing = useCallback(() => {
    setState((current) => ({ ...current, phase: "processing" }));
  }, []);
  const enterFinalizing = useCallback(() => {
    setState((current) => ({ ...current, phase: "finalizing" }));
  }, []);
  const enterTerminal = useCallback(() => {
    setState((current) => ({ ...current, phase: "terminal" }));
  }, []);
  const enterUnavailable = useCallback(() => {
    setState((current) => ({ ...current, phase: "unavailable" }));
  }, []);

  const goNextKyc = useCallback((lastIndex: number) => {
    setState((current) => {
      if (current.phase !== "kyc") {
        return current;
      }
      if (current.kycIndex >= lastIndex) {
        return { ...current, phase: "processing" };
      }
      return { ...current, kycIndex: current.kycIndex + 1 };
    });
  }, []);

  const goBackKyc = useCallback(() => {
    setState((current) => ({ ...current, kycIndex: Math.max(current.kycIndex - 1, 0) }));
  }, []);

  return {
    phase: state.phase,
    kycIndex: state.kycIndex,
    enterKyc,
    enterProcessing,
    enterFinalizing,
    enterTerminal,
    enterUnavailable,
    goNextKyc,
    goBackKyc,
  };
}
